import {
  DataGrid as Grid, GridColumnProps,
  MenuItem,
  SelectFilterComponent,
  SelectFloatingFilterComponent,
  SubmitMenuItem,
} from '@roc/ui';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useRoutes } from '../../../hooks/useRoutes';
import { IconButton, ListItemIcon, ListItemText, Menu } from '@material-ui/core';
import { MoreVert, Assignment, FileCopy, Delete } from '@material-ui/icons';
import { AssignCadenceTemplateModal } from './assignCadenceTemplate';
import { useStore } from '@roc/client-portal-shared/hooks';
import { CadenceTemplateGrid } from '@roc/feature-types';



const DEFAULT_SORT_MODEL = [{ colId: 'cadenceLastUpdatedDate', sort: 'desc' }];

const columns: GridColumnProps[] = [
  {
    field: 'cadence',
    headerName: 'Cadence',
    minWidth: 180,
    cellRenderer: 'cadenceNameRenderer',
    comparator: () => 0,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'numberOfSteps',
    headerName: 'Number of Steps',
    minWidth: 120,
  },
  {
    field: 'cadenceCreatedDate',
    headerName: 'Created Date',
    minWidth: 120,
  },
  {
    field: 'cadenceCreatedBy',
    headerName: 'Created By',
    minWidth: 180,
  },
  {
    field: 'cadenceLastUpdatedDate',
    headerName: 'Last updated Date',
    minWidth: 120,
    comparator: () => 0,
  },
  {
    headerName: 'Action',
    minWidth: 80,
    cellRenderer: 'actionCellRenderer',
  },
];


export const CadenceTemplatesSection = observer(() => {
  const { cadenceRoutesConfig } = useRoutes();
  const { cadenceStore } = useStore();
  //const [cadenceRows, setCadenceRows] = useState([]);
  const [selectedCadence, setSelectedCadence] = useState<CadenceTemplateGrid | null>(null);
  const { push } = useHistory();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const cadenceRows = cadenceStore.templatesRows;


  const frameworkComponents = {
    selectFilter: SelectFilterComponent,
    selectFloatingFilter: SelectFloatingFilterComponent,
    cadenceNameRenderer: params => {
      const cadenceId = params?.data?.cadenceId?.toString();
      if (!cadenceId) return null;

      const cadenceDetailUrl = cadenceRoutesConfig.cadenceDetailsBackofficeLead(cadenceId).children.cadenceDetailBackofficeLead.url;
      return (
        <a
          href={cadenceDetailUrl}
          style={{ textDecoration: 'underline', color: '#5c94de', cursor: 'pointer' }}
          onClick={(e) => {
            e.preventDefault();
            push(cadenceDetailUrl);
          }}
        >
          {params.value}
        </a>
      );
    },
    actionCellRenderer: params => {
      return (
        <>
          <IconButton onClick={e => openMenu(e, params.node.data)}>
            <MoreVert color="primary" />
          </IconButton>
        </>
      );
    },
  };


  const openMenu = (event: React.MouseEvent<HTMLElement>, record) => {
    setAnchorEl(event.currentTarget);
    setSelectedCadence(record);
  };


  const onFilterChanged = params => {
    const filterModel = params.api.getFilterModel();
    cadenceStore.cadenceTemplatesGridStore.setFilterModel(filterModel);
  };


  const onSortChanged = params => {
    const sortModel = params.api.getSortModel();
    cadenceStore.cadenceTemplatesGridStore.setSortModel(sortModel);
  };


  useEffect(() => {
    // the grid store is shared with My Cadences: start clean, most recently updated first
    cadenceStore.cadenceTemplatesGridStore.reset();
    cadenceStore.cadenceTemplatesGridStore.setSortModel(DEFAULT_SORT_MODEL);
  }, []);

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCopyCadence = () => {
    cadenceStore.copyCadence(selectedCadence.cadenceId)
  };

  const handleSubmitUsersAssociation = () => {
    cadenceStore.assignUsersToCadence(selectedCadence.cadenceId);
    handleMenuClose();
  }

  return (
    <>
      <Grid
        columns={columns}
        rows={cadenceRows}
        frameworkComponents={frameworkComponents}
        onSortChanged={onSortChanged}
        onFilterChanged={onFilterChanged}
        isLoading={cadenceStore.cadenceTemplatesGridStore?.isLoading}
        showFilters={cadenceStore.cadenceTemplatesGridStore?.showFilters}
        sortModel={cadenceStore.cadenceTemplatesGridStore?.sortModel}
        filterModel={cadenceStore.cadenceTemplatesGridStore?.filterModel}
        paginationData={cadenceStore.cadenceTemplatesGridStore?.gridData.meta}
        setPageNumber={cadenceStore.cadenceTemplatesGridStore?.setPageNumber}
      />
      <Menu
        id="menu-appbar"
        getContentAnchorEl={null}
        anchorEl={anchorEl}
        keepMounted
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        open={menuOpen}
        onClose={handleMenuClose}
      >
        < SubmitMenuItem
          disabled={selectedCadence?.selectedList === null || selectedCadence?.numberOfSteps === null || selectedCadence?.numberOfSteps < 1 }
          testId={`assign-template`}
          onClick={handleSubmitUsersAssociation}
          submitDialogProps={{
            title: `Share Cadence`,
            body: <AssignCadenceTemplateModal selectedCadenceId={selectedCadence?.cadenceId} />,
            okButtonText: 'Submit',
          }}
        >
          <ListItemIcon>
            <Assignment fontSize="small" />
          </ListItemIcon>
          <ListItemText onClick={handleMenuClose}>Share with Originators</ListItemText>
        </SubmitMenuItem>

        <MenuItem
          testId={`copy-template`}
          onClick={() => {
            handleCopyCadence();
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <FileCopy fontSize="small" />
          </ListItemIcon>
          <ListItemText >Copy Cadence</ListItemText>
        </MenuItem>

        <SubmitMenuItem
          testId={`delete-template`}
          onClick={() => {
            cadenceStore.deleteCadence(selectedCadence.cadenceId);
            handleMenuClose();
          }}
          submitDialogProps={{
            title: `Delete Cadence`,
            body: (
              <div style={{ padding: "8px 0" }}>
                Are you sure you want to delete this cadence?
              </div>
            ),
            okButtonText: 'Delete',
          }}
        >
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete Cadence</ListItemText>
        </SubmitMenuItem>
      </Menu>
    </>

  );
});
